import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Upload,
} from "antd";
import React, { useEffect, useState } from "react";
import { FaEdit, FaEye, FaPlus, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { CameraOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { asyncStatus } from "../../utils/asyncStatus";
import { getAllProductsAsync } from "../../services/ProductManagementServices";

const ProductManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [storeData] = useState({ logo: "" });
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const { get_all_products_data, get_all_products_status } = useSelector(
    (state) => state.productManagement
  );

  const { user } = useSelector((state) => state.userAuth);
  const productPermissions = user?.permissions?.products || {};

  const canCreate = productPermissions.create;
  const canUpdate = productPermissions.update;
  const canRead = productPermissions.read;

  const productLoader = get_all_products_status === asyncStatus.LOADING;

  useEffect(() => {
    if (canRead) {
      dispatch(
        getAllProductsAsync({
          page: pagination.current,
          limit: pagination.pageSize,
          search: search.trim(),
        })
      );
    }
  }, [dispatch, canRead, pagination.current, pagination.pageSize, search]);

  useEffect(() => {
    if (get_all_products_data) {
      setPagination({
        ...pagination,
        total: get_all_products_data.totalCount || 0,
      });
    }
  }, [get_all_products_data]);

  const handleTableChange = (newPagination) => {
    setPagination({
      ...newPagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const columns = [
    {
      title: "Product Name",
      key: "name",
      render: (record) => (
        <Space>
          <img
            src={
              record?.images?.[0] ||
              "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg"
            }
            alt="logo"
            style={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              objectFit: "contain",
            }}
          />
          <span>{record?.name}</span>
        </Space>
      ),
    },
    {
      title: "Collection",
      key: "category",
      render: (record) => (
        <Space>
          {record?.category
            ?.map((e) => e?.name)
            .filter(Boolean)
            .join(", ")}
        </Space>
      ),
    },
    {
      title: "Availability",
      dataIndex: "availability",
      key: "availability",
      render: (availability) => (
        <Tag color={availability === "In-Stock" ? "green" : "red"}>
          {availability}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          {productPermissions.read && (
            <Button
              onClick={() =>
                navigate(`/products/product-detail/${record?._id}`)
              }
              icon={<FaEye />}
            />
          )}
          {productPermissions.update && (
            <Button
              onClick={() =>
                navigate(`/products/add-products`, {
                  state: record,
                })
              }
              icon={<FaEdit />}
            />
          )}
        </Space>
      ),
    },
  ];

  if (!canRead) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Empty
          image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
          description={
            <span className="text-gray-600 text-lg">
              You don't have permission to Access this
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-3xl font-bold text-center mb-4">Products</h1>

      <div className="flex justify-between my-4">
        <Input
          placeholder="Search by name"
          prefix={<FaSearch />}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "300px" }}
        />
        {productPermissions.create && (
          <Button
            type="primary"
            icon={<FaPlus />}
            onClick={() => navigate("/products/add-products")}
          >
            Add Product
          </Button>
        )}
      </div>

      {productLoader ? (
        <div className="flex items-center justify-center h-[40vh]">
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={get_all_products_data?.products || []}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "15", "20", "50"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
          rowKey="_id"
        />
      )}

      <Modal
        title="Edit Store"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <div className="text-center">
            <Upload
              accept="image/*"
              showUploadList={false}
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => onSuccess("ok"), 0);
              }}
            >
              <div className="relative inline-block group cursor-pointer">
                <div className="w-32 h-32 rounded-lg border-2 border-gray-200 shadow-md overflow-hidden mx-auto">
                  {imageLoading ? (
                    <Spin
                      size="large"
                      className="flex items-center justify-center h-40"
                    />
                  ) : storeData.logo ? (
                    <img
                      src={storeData.logo}
                      alt="Store Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full bg-gray-100">
                      <CameraOutlined className="text-2xl text-gray-500" />
                    </div>
                  )}
                </div>
              </div>
            </Upload>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductManagement;